import { siteMeta } from "../pages/site/content";

/** Public website origin (no trailing slash). */
export function getSiteOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    const host = window.location.hostname || "";
    if (host && host !== "localhost" && host !== "127.0.0.1") {
      return window.location.origin.replace(/\/$/, "");
    }
  }
  return (siteMeta.siteUrl || "https://celszc.org").replace(/\/$/, "");
}

export function absoluteUrl(path = "/") {
  const origin = getSiteOrigin();
  if (!path || path === "/") return `${origin}/`;
  if (/^https?:\/\//i.test(path)) return path;
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  const selector = attr === "property" ? `meta[property="${key}"]` : `meta[name="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Apply document title + meta tags for the current page.
 */
export function applySeo({
  title,
  description,
  path = "/",
  image,
  noindex = false,
  type = "website",
  jsonLd,
} = {}) {
  if (typeof document === "undefined") return;

  const fullName = siteMeta.fullName;
  const shortName = siteMeta.name;
  const pageTitle = title
    ? `${title} | ${shortName} — ${fullName}`
    : `${shortName} — ${fullName}`;
  const desc =
    description ||
    siteMeta.description ||
    `${fullName}. ${siteMeta.tagline}`;
  const url = absoluteUrl(path);
  const ogImage = absoluteUrl(image || siteMeta.ogImage || "/favicon.png");

  document.title = pageTitle;

  upsertMeta("name", "description", desc);
  upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
  upsertMeta("name", "author", fullName);
  upsertMeta("name", "theme-color", "#0b1e7a");

  upsertMeta("property", "og:type", type);
  upsertMeta("property", "og:site_name", fullName);
  upsertMeta("property", "og:title", pageTitle);
  upsertMeta("property", "og:description", desc);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", ogImage);
  upsertMeta("property", "og:locale", "en_NG");

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", pageTitle);
  upsertMeta("name", "twitter:description", desc);
  upsertMeta("name", "twitter:image", ogImage);

  upsertLink("canonical", url);
  upsertJsonLd("celszc-jsonld", jsonLd || null);
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Church",
    name: siteMeta.fullName,
    alternateName: siteMeta.name,
    url: getSiteOrigin(),
    description: siteMeta.description || siteMeta.tagline,
    email: siteMeta.email || undefined,
    telephone: siteMeta.phone || undefined,
    address: siteMeta.address
      ? {
          "@type": "PostalAddress",
          addressLocality: "Lagos",
          addressCountry: "NG",
          streetAddress: siteMeta.address,
        }
      : undefined,
    sameAs: [],
  };
}
